import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
    Container,
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    Button,
    IconButton,
    CircularProgress,
    Dialog,
    Alert
} from '@mui/material';
import {
    Close as CloseIcon,
    PlayArrow as PlayArrowIcon,
    VideoLibrary,
    PhotoLibrary,
    ZoomIn
} from '@mui/icons-material';
import './GuestGallery.css';
import useRouteMeta from '../../../hooks/useRouteMeta';
import Header from '../../Website/Header';

// Static Data Configuration
const STATIC_IMAGES = [
    {
        id: 1,
        src: "/assets/img/guest_login/necklace.png",
        title: "Luxury Necklace",
        description: "Elegant diamond necklace design"
    },
    {
        id: 2,
        src: "/assets/img/guest_login/ring.jpg",
        title: "Modern Ring",
        description: "Stunning gold ring with intricate details"
    },
    {
        id: 3,
        src: "/assets/img/guest_login/earring.png",
        title: "Exquisite Earrings",
        description: "Beautiful handcrafted earrings"
    },
    {
        id: 4,
        src: "/assets/img/guest_login/bangles.png",
        title: "Traditional Bangles",
        description: "Classic bangle set for special occasions"
    },
    {
        id: 5,
        src: "/assets/img/guest_login/mangalsutra.png",
        title: "Classic Mangalsutra",
        description: "Timeless symbol of elegance"
    },
    {
        id: 6,
        src: "/assets/img/guest_login/bust_display.jpg",
        title: "Jewelry Display",
        description: "Professional showcase presentation"
    }
];

const STATIC_VIDEOS = [
    {
        id: 1,
        src: "/assets/img/guest_login/vid1.mp4",
        title: "Jewelry Showcase",
        description: "Dynamic view of our collection",
        media_type: 'video'
    },
    {
        id: 2,
        src: "/assets/img/guest_login/vid2.mp4",
        title: "Product Animation",
        description: "Cinematic presentation of elegant pieces",
        media_type: 'video'
    }
];


// Component to render media with loading state and badges - Match Gallery.js style
const GuestMediaItem = ({ item, onClick }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const isVideo = item.media_type === 'video';

    // For videos, append #t=0.1 to force first frame as thumbnail on mobile
    const mediaUrl = isVideo ? `${item.src}#t=0.1` : item.src;
    const isVideoFile = item.src.endsWith('.mp4');

    // For video thumbnails, we can use a placeholder or try to capture a frame. 
    // To keep it simple for static assets without thumbnails, we'll use a generic placeholder or the video itself if supported.
    // Ideally, for production, static video thumbnails should be provided.
    // Here we will just try to show the video element as thumbnail (muted) or an icon if it fails.

    const handleLoad = () => {
        setLoading(false);
    };

    const handleError = () => {
        setLoading(false);
        setError(true);
    };

    return (

        <Box
            onClick={() => onClick(item)}
            className="guest-media-wrapper"
            sx={{
                position: 'relative',
                cursor: 'pointer',
                borderRadius: '20px', // Full rounded corners as there is no content below
                overflow: 'hidden',
                aspectRatio: '3/4', // Consistent vertical aspect ratio
                width: '100%',
                backgroundColor: '#f5f5f5',
                boxShadow: '0 10px 40px -10px rgba(0,0,0,0.1)', // Premium shadow
                transition: 'transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.4s ease',
                '&:hover': {
                    transform: 'translateY(-8px)', // Lift effect
                    boxShadow: '0 20px 50px -10px rgba(99, 24, 198, 0.3)', // Colored shadow on hover
                },
                '&:hover .guest-media-zoom': {
                    transform: 'scale(1.05)',
                },
                '&:hover .guest-media-overlay': {
                    opacity: 1,
                }
            }}
        >
            {/* Loading State */}
            {loading && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1
                    }}
                >
                    <CircularProgress size={30} sx={{ color: '#6318C6' }} />
                </Box>
            )}

            {/* Error State */}
            {error && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'text.secondary',
                        flexDirection: 'column',
                        zIndex: 1
                    }}
                >
                    <Typography variant="caption">Failed to load</Typography>
                </Box>
            )}

            {/* Image/Thumbnail */}
            {isVideo ? (
                <Box
                    component="video"
                    src={mediaUrl}
                    className="guest-media-zoom"
                    onLoadedData={handleLoad}
                    onError={handleError}
                    muted
                    loop
                    playsInline
                    preload="metadata"
                    onMouseOver={event => event.target.play()}
                    onMouseOut={event => event.target.pause()}
                    sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        objectPosition: 'center',
                        transition: 'transform 0.7s cubic-bezier(0.4, 0, 0.2, 1)',
                        opacity: loading ? 0 : 1,
                    }}
                />
            ) : (
                <Box
                    component="img"
                    src={mediaUrl}
                    alt={item.title}
                    className="guest-media-zoom"
                    onLoad={handleLoad}
                    onError={handleError}
                    sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        objectPosition: 'center',
                        transition: 'transform 0.7s cubic-bezier(0.4, 0, 0.2, 1)',
                        opacity: loading ? 0 : 1,
                    }}
                />
            )}


            {/* Type Badge - Glassmorphism Style */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 15, // Moved to top right for better visibility without text
                    right: 15,
                    zIndex: 2,
                    bgcolor: isVideo ? 'rgba(59, 130, 246, 0.9)' : 'rgba(16, 185, 129, 0.9)',
                    backdropFilter: 'blur(8px)',
                    color: 'white',
                    px: 1.5,
                    py: 0.75,
                    borderRadius: '30px', /* Pill shape */
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    letterSpacing: '0.5px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                    border: '1px solid rgba(255,255,255,0.3)'
                }}
            >
                {isVideo ? (
                    <>
                        <VideoLibrary sx={{ fontSize: 14, mr: 0.6 }} />
                        <Typography variant="caption" sx={{ fontWeight: 700, lineHeight: 1 }}>VIDEO</Typography>
                    </>
                ) : (
                    <>
                        <PhotoLibrary sx={{ fontSize: 14, mr: 0.6 }} />
                        <Typography variant="caption" sx={{ fontWeight: 700, lineHeight: 1 }}>IMAGE</Typography>
                    </>
                )}
            </Box>

            {/* Hover Overlay with Gradient */}
            <Box
                className="guest-media-overlay"
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(to top, rgba(99, 24, 198, 0.4), rgba(0,0,0,0))',
                    backdropFilter: 'blur(0px)',
                    opacity: 0,
                    transition: 'all 0.4s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 2,
                    pointerEvents: 'none' // Prevent blocking clicks
                }}
            >
                {isVideo ? (
                    <Box
                        sx={{
                            width: 70,
                            height: 70,
                            borderRadius: '50%',
                            bgcolor: 'rgba(255,255,255,0.95)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
                            transform: 'scale(0.9)',
                            transition: 'transform 0.3s ease'
                        }}
                    >
                        <PlayArrowIcon sx={{ fontSize: 44, color: '#6318C6', ml: 0.5 }} />
                    </Box>
                ) : (
                    <Box
                        sx={{
                            width: 60,
                            height: 60,
                            borderRadius: '50%',
                            bgcolor: 'rgba(255,255,255,0.95)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
                            transform: 'scale(0.9)',
                            transition: 'transform 0.3s ease'
                        }}
                    >
                        <ZoomIn sx={{ fontSize: 36, color: '#6318C6' }} />
                    </Box>
                )}
            </Box>
        </Box>
    );
};

const GuestGallery = () => {
    // SEO Meta Tags
    useRouteMeta();

    const navigate = useNavigate();
    // No loading state needed for static assets
    const [images] = useState(STATIC_IMAGES);
    const [videos] = useState(STATIC_VIDEOS);
    const [selectedMedia, setSelectedMedia] = useState(null);
    const [previewOpen, setPreviewOpen] = useState(false);

    // Carousel State (Ported from LandingPage.js)
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [isTransitioning, setIsTransitioning] = useState(false);

    // Carousel Images (Ported from LandingPage.js)
    const carouselImages = [
        { src: "/assets/img/website/carousel-1.svg", alt: "Glammify creative" },
        { src: "/assets/img/website/carousel-2.svg", alt: "Glammify creative 2" },
        { src: "/assets/img/website/carousel-3.svg", alt: "Glammify creative 3" },
        { src: "/assets/img/website/carousel-4.svg", alt: "Glammify creative 3" },
        { src: "/assets/img/website/carousel-5.svg", alt: "Glammify creative 3" }
    ];

    // Carousel Effect (Ported from LandingPage.js)
    useEffect(() => {
        if (carouselImages.length <= 1) return;

        const interval = setInterval(() => {
            setIsTransitioning(true);

            setTimeout(() => {
                setCurrentImageIndex((prevIndex) =>
                    (prevIndex + 1) % carouselImages.length
                );
                setIsTransitioning(false);
            }, 500); // Half second for fade out
        }, 4000); // 4 seconds total cycle

        return () => clearInterval(interval);
    }, [carouselImages.length]);

    const handleMediaClick = (media) => {
        setSelectedMedia(media);
        setPreviewOpen(true);
    };

    const handleClosePreview = () => {
        setPreviewOpen(false);
        setSelectedMedia(null);
    };

    return (
        <div className="guest-gallery-page">
            <Header />

            {/* Header Section - Matches LandingPage header-gradient */}
            <section className='header-gradient position-relative'>
                <div className='container-fluid'>
                    <div className='col-md-12 carousel-section'>
                        <img
                            src="/assets/img/website/star.svg"
                            alt="Glammify Logo"
                            className="sparkle-image"
                        />
                        <div className='row'>
                            <div className='col-md-10 col-12 col-lg-7 section-content'>
                                <h2>Where Jewellery Meets<br /> <span className='font-montserrat'>AI</span> Glamour</h2>
                                <p>Bring your designs to life on stunning AI models <br /><span>No shoots, No studios, No waiting around</span><br />
                                    Just upload, customise, and go live</p>
                                <Link className="btn btn-secondary" to="/login">Login/Sign up</Link>
                            </div>
                        </div>
                    </div>
                </div>
                <div className='header-image-anim'>
                    <div className={`carousel-container ${isTransitioning ? 'fade-out' : 'fade-in'}`}>
                        <img
                            src={carouselImages[currentImageIndex].src}
                            alt={carouselImages[currentImageIndex].alt}
                            className="img-fluid carousel-image"
                        />
                    </div>
                </div>
            </section>

            {/* Steps Section - Copied from LandingPage */}
            <section className='sections bg-white steps-section'>
                <Container maxWidth={false} className='container-fluid'>
                    <div className='row justify-content-center'>
                        <div className='col-md-12 col-lg-9 col-xl-7 text-center section-headings'>
                            <h2 className='font-heading-guest'>Turn a Product Photo Into a Glam Look In Just <span className='font-montserrat'>3</span> Easy Steps</h2>
                        </div>
                    </div>
                    <div className='row justify-content-center'>
                        <div className='col-md-12 col-lg-12'>
                            <div className="process-steps-container">
                                <div className="row">
                                    {/* Step 1: Upload */}
                                    <div className="col-md-4">
                                        <div className="process-step-card">
                                            <div className="step-image">
                                                <img src="/assets/img/website/Step-1.gif" alt="Upload" className='img-fluid' />
                                            </div>
                                        </div>
                                        <div className="step-info text-center">
                                            <span className="step-number">Step 1</span>
                                            <h4>Upload</h4>
                                            <p>Start by uploading a photo of your jewellery. Necklaces, rings, bangles, earrings-bring it all</p>
                                        </div>
                                    </div>

                                    {/* Step 2: Choose Your Style */}
                                    <div className="col-md-4">
                                        <div className="process-step-card">
                                            <div className="step-image">
                                                <img src="/assets/img/website/Step-2.gif" alt="Choose Style" className='img-fluid' />
                                            </div>
                                        </div>
                                        <div className="step-info text-center">
                                            <span className="step-number">Step 2</span>
                                            <h4>Choose Your Style</h4>
                                            <p>Pick the model look you want- choose gender, ethnicity, background & media</p>
                                        </div>
                                    </div>

                                    {/* Step 3: Get Your Creatives */}
                                    <div className="col-md-4">
                                        <div className="process-step-card">
                                            <div className="step-image">
                                                <img src="/assets/img/website/Step-3.jpg" alt="Get Creatives" className='img-fluid' />
                                            </div>
                                        </div>
                                        <div className="step-info text-center">
                                            <span className="step-number">Step 3</span>
                                            <h4>Get Your Creatives</h4>
                                            <p>In just a few minutes, get studio-quality images or videos of your piece, modelled and ready to share</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Container>
            </section>

            {/* Sample Images Section */}
            <section className="sections guest-samples-section bg-linear-gradient">
                <Container maxWidth="lg">
                    <Box className="section-header text-center mb-5">
                        <Typography variant="h2" className="font-heading section-title">
                            Sample AI-Generated Images
                        </Typography>
                        <Typography variant="body1" className="section-description">
                            Stunning jewelry showcases created in minutes
                        </Typography>
                    </Box>

                    <Grid container spacing={3}>
                        {images.map((image) => (
                            <Grid item xs={12} sm={6} md={4} key={image.id}>
                                <GuestMediaItem
                                    item={image}
                                    onClick={handleMediaClick}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </section>

            {/* Sample Videos Section */}
            <section className="sections guest-samples-section guest-videos-section bg-white">
                <Container maxWidth="lg">
                    <Box className="section-header text-center mb-5">
                        <Typography variant="h2" className="font-heading section-title">
                            Sample AI-Generated Videos
                        </Typography>
                        <Typography variant="body1" className="section-description">
                            Dynamic video presentations that bring jewelry to life
                        </Typography>
                    </Box>

                    <Grid container spacing={3}>
                        {videos.map((video) => (
                            <Grid item xs={12} sm={6} md={4} key={video.id}>
                                <GuestMediaItem
                                    item={video}
                                    onClick={handleMediaClick}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </section>

            {/* CTA Section */}
            <section className="guest-cta-section" style={{ padding: '80px 0' }}>
                <Container maxWidth="md">
                    <Box className="cta-content text-center">
                        <Typography variant="h2" className="font-heading cta-title mb-4">
                            Ready to Create Your Own?
                        </Typography>
                        <Typography variant="body1" className="cta-description mb-4">
                            Join Glammify and start generating professional jewelry visuals in minutes
                        </Typography>
                        <Box className="cta-buttons d-flex justify-content-center gap-3">
                            <Button
                                variant="contained"
                                size="large"
                                className="cta-button primary btn btn-cta-primary font-large"
                                onClick={() => navigate('/login')}
                            >
                                Login/Sign up
                            </Button>
                        </Box>
                    </Box>
                </Container>
            </section>

            {/* Preview Dialog */}
            <Dialog
                open={previewOpen}
                onClose={handleClosePreview}
                maxWidth="md"
                fullWidth
                sx={{
                    '& .MuiDialog-paper': {
                        width: { xs: '95vw', sm: '90vw', md: '60vw' },
                        maxWidth: { xs: '95vw', sm: '90vw', md: '600px' },
                        margin: { xs: '10px', sm: '20px', md: 'auto' },
                        maxHeight: '90vh',
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                        overflow: 'visible'
                    }
                }}
                className="preview-dialog"
            >
                <Box sx={{ position: 'relative' }}>
                    <IconButton
                        onClick={handleClosePreview}
                        sx={{
                            position: 'absolute',
                            top: -15,
                            right: -15,
                            bgcolor: 'rgba(0,0,0,0.5)',
                            color: 'white',
                            backdropFilter: 'blur(4px)',
                            '&:hover': {
                                bgcolor: 'rgba(0,0,0,0.7)',
                            },
                            width: 36,
                            height: 36,
                            zIndex: 10,
                            border: '1px solid rgba(255,255,255,0.2)'
                        }}
                    >
                        <CloseIcon sx={{ fontSize: 20 }} />
                    </IconButton>

                    <Box className="preview-content" sx={{ borderRadius: 4, overflow: 'hidden', bgcolor: 'transparent', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)' }}>
                        {selectedMedia && (
                            <Box className="preview-media-container" sx={{ bgcolor: 'black', display: 'flex', justifyContent: 'center' }}>
                                {selectedMedia.media_type === 'video' ? (
                                    <video
                                        src={selectedMedia.src}
                                        controls
                                        autoPlay
                                        className="preview-media"
                                        style={{ width: '100%', height: 'auto', maxHeight: '85vh', display: 'block' }}
                                    />
                                ) : (
                                    <img
                                        src={selectedMedia.src}
                                        alt="Preview"
                                        className="preview-media"
                                        style={{ width: '100%', height: 'auto', maxHeight: '85vh', display: 'block', objectFit: 'contain' }}
                                    />
                                )}
                            </Box>
                        )}
                    </Box>
                </Box>
            </Dialog>

            {/* Footer */}
            <Box className="guest-footer sections bg-white py-4 border-top">
                <Container maxWidth="lg">
                    <Typography variant="body2" color="text.secondary" align="center">
                        © {new Date().getFullYear()} Glammify. All rights reserved.
                    </Typography>
                </Container>
            </Box>
        </div>
    );
};

export default GuestGallery;
